import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarDays, CheckCircle2, Clock, Loader2, MapPin, Pencil, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
  getOrganizerEvents,
  requestOrganizerHistoryLink,
  type OrganizerEvent,
} from "@/lib/events/organizer-history.functions";

export const Route = createFileRoute("/my-events")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search["token"] === "string" ? (search["token"] as string) : undefined,
  }),
  head: () => {
    const url = "https://whatfinland.com/my-events";
    const title = "My Event Submissions — What Finland";
    const description =
      "Organizers: check the review status of every event you submitted to What Finland — pending, live, edited or not published.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "noindex" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: MyEventsPage,
  errorComponent: ({ error }) => (
    <div className="p-10 text-center text-muted-foreground">{(error as Error).message}</div>
  ),
  notFoundComponent: () => <div className="p-10 text-center text-muted-foreground">Not found.</div>,
});

const STATUS: Record<
  OrganizerEvent["status"],
  { label: string; className: string; Icon: typeof Clock }
> = {
  pending: {
    label: "In Review",
    className: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    Icon: Clock,
  },
  live: {
    label: "Live",
    className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    Icon: CheckCircle2,
  },
  edited: {
    label: "Live (Edited)",
    className: "bg-sky-500/10 text-sky-600 border-sky-500/20",
    Icon: Pencil,
  },
  rejected: {
    label: "Not Published",
    className: "bg-destructive/10 text-destructive border-destructive/20",
    Icon: XCircle,
  },
};

function fmt(dt?: string | null) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    timeZone: "Europe/Helsinki",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function MyEventsPage() {
  const { token } = Route.useSearch();
  const fetchEvents = useServerFn(getOrganizerEvents);
  const requestLink = useServerFn(requestOrganizerHistoryLink);
  const [email, setEmail] = useState("");

  const query = useQuery({
    queryKey: ["organizer-events", token],
    queryFn: () => fetchEvents({ data: { token: token! } }),
    enabled: Boolean(token),
    retry: false,
  });

  const linkMutation = useMutation({
    mutationFn: (value: string) => requestLink({ data: { email: value } }),
    onSuccess: () =>
      toast.success("Check your inbox — we sent a private link to your event history."),
    onError: (e: Error) => toast.error(e.message || "Couldn't send the link."),
  });

  const events = query.data?.events ?? [];
  const counts = {
    total: events.length,
    live: events.filter((e) => e.status === "live" || e.status === "edited").length,
    pending: events.filter((e) => e.status === "pending").length,
    rejected: events.filter((e) => e.status === "rejected").length,
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:py-14">
      <header className="mb-8">
        <h1 className="text-3xl font-heading font-bold tracking-tight sm:text-4xl">
          My Event Submissions
        </h1>
        <p className="mt-2 text-muted-foreground">
          Track everything you've submitted to What Finland and see where each event stands in
          review.
        </p>
      </header>

      {token && query.isLoading && (
        <div className="flex items-center gap-2 rounded-2xl border bg-card p-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your events…
        </div>
      )}

      {token && query.isError && (
        <div className="mb-8 rounded-2xl border border-destructive/20 bg-destructive/5 p-5 text-sm text-destructive">
          {(query.error as Error).message}
        </div>
      )}

      {query.data && (
        <section className="mb-10">
          <div className="mb-5 flex flex-wrap gap-3 text-sm">
            <span className="rounded-full border bg-card px-3 py-1">
              {counts.total} Submitted
            </span>
            <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-emerald-600">
              {counts.live} Live
            </span>
            <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1 text-amber-600">
              {counts.pending} In Review
            </span>
            {counts.rejected > 0 && (
              <span className="rounded-full border border-destructive/20 bg-destructive/10 px-3 py-1 text-destructive">
                {counts.rejected} Not Published
              </span>
            )}
          </div>

          {events.length === 0 ? (
            <div className="rounded-2xl border bg-card p-6 text-muted-foreground">
              No submissions found for {query.data.email}.{" "}
              <Link to="/submit-event" className="underline">
                Submit an event
              </Link>
              .
            </div>
          ) : (
            <ul className="space-y-4">
              {events.map((e) => {
                const s = STATUS[e.status];
                return (
                  <li
                    key={e.id}
                    className="flex gap-4 rounded-2xl border bg-card p-4 transition-shadow hover:shadow-md"
                  >
                    {e.event_image && (
                      <img
                        src={e.event_image}
                        alt={e.title}
                        loading="lazy"
                        className="hidden h-24 w-24 shrink-0 rounded-xl object-cover sm:block"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <h2 className="text-lg font-semibold leading-snug">{e.title}</h2>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${s.className}`}
                        >
                          <s.Icon className="h-3.5 w-3.5" />
                          {s.label}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                        {e.start_date && (
                          <p className="flex items-center gap-1.5">
                            <CalendarDays className="h-3.5 w-3.5" />
                            {[fmt(e.start_date), fmt(e.end_date)].filter(Boolean).join(" → ")}
                          </p>
                        )}
                        {(e.venue || e.city) && (
                          <p className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5" />
                            {[e.venue, e.city].filter(Boolean).join(", ")}
                          </p>
                        )}
                        {e.submitted_at && (
                          <p className="text-xs">Submitted {fmt(e.submitted_at)}</p>
                        )}
                      </div>
                      {e.last_note && (
                        <p className="mt-3 rounded-lg border-l-2 border-border bg-muted/50 px-3 py-2 text-sm">
                          {e.last_note}
                        </p>
                      )}
                      {e.approved && e.slug && (
                        <Link
                          to="/happening/$slug"
                          params={{ slug: e.slug }}
                          className="mt-3 inline-block text-sm font-medium underline"
                        >
                          View It Live
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      <section className="rounded-2xl border bg-card p-6">
        <h2 className="text-lg font-semibold">
          {query.data ? "Get A Fresh Link" : "Find My Submissions"}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter the email you used when submitting. We'll email you a private link — it works for 7
          days.
        </p>
        <form
          className="mt-4 flex flex-col gap-3 sm:flex-row"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (email.trim()) linkMutation.mutate(email.trim());
          }}
        >
          <input
            type="email"
            required
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="you@example.com"
            className="h-11 flex-1 rounded-xl border bg-background px-4 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="submit"
            disabled={linkMutation.isPending}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
          >
            {linkMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Email My Link
          </button>
        </form>
        <p className="mt-3 text-xs text-muted-foreground">
          If we have submissions for that address, the link is on its way.
        </p>
      </section>
    </div>
  );
}
